import type { FunctionComponent } from 'react';
import React, { useEffect, useState } from 'react';
import type { SourceType } from '@trello/atlassian-analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace } from '@trello/i18n';
import { Badge } from '@trello/nachos/badge';
import type { NewFeature } from './FeatureRolloutConfig';
import { useNewFeature } from './useNewFeature';

const format = forNamespace('new feature');

interface NewPillProps {
  featureId: NewFeature;
  source: SourceType;
  /**
   * Whether to fire a oneTimeMessagesDismissed indicating that the new feature
   * has been seen when this component renders.
   * @default true
   */
  markViewed?: boolean;
}

export const NewPill: FunctionComponent<NewPillProps> = ({
  featureId,
  source,
  markViewed = true,
}) => {
  const { isNewFeature, acknowledgeNewFeature } = useNewFeature(featureId);
  const [shouldMarkViewed, setShouldMarkedViewed] = useState(markViewed);

  useEffect(() => {
    if (shouldMarkViewed && isNewFeature) {
      acknowledgeNewFeature({ source });
      setShouldMarkedViewed(false);
      Analytics.sendViewedComponentEvent({
        componentType: 'pill',
        componentName: 'newFeaturePill',
        source,
        attributes: { featureId },
      });
    }
  }, [
    featureId,
    isNewFeature,
    acknowledgeNewFeature,
    shouldMarkViewed,
    setShouldMarkedViewed,
    source,
  ]);

  if (!isNewFeature) {
    return null;
  }

  return <Badge appearance="added">{format('new')}</Badge>;
};
